import PropTypes from 'prop-types'
import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { ProtectedRoute, useAuth } from '@/features/authentication'
import { LoadingFallback } from '@/features/shared-ui'
import { APP_ROUTES, AUTH_ROUTES, DASHBOARD_ROUTES } from './routeConstants'

// Lazy-loaded page modules, imported directly (not via feature barrels)
// so each page code-splits into its own chunk.
const LoginPage = lazy(() => import('@/features/authentication/pages/LoginPage'))
const DashboardLayout = lazy(() => import('@/features/dashboard/pages/DashboardLayout'))
const AllSites = lazy(() => import('@/features/dashboard/pages/all-sites/AllSitesPageV2'))
const SiteDashboard = lazy(() => import('@/features/dashboard/pages/site/SiteDashboardV2'))
const VehicleDashboard = lazy(() => import('@/features/dashboard/pages/vehicle/VehicleDashboardV2'))
const ControlStationDashboard = lazy(
  () => import('@/features/dashboard/pages/control-station/ControlStationDashboard')
)

const ALL_SITES_PATH = `${APP_ROUTES.DASHBOARD}/${DASHBOARD_ROUTES.ALL_SITES}`

// Suspense sits at the route level (not around <Routes>) so a lazy page
// loading inside the dashboard shows the fallback in the layout's Outlet
// instead of blanking the whole layout.
function Page({ children }) {
  return <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
}

Page.propTypes = {
  children: PropTypes.node.isRequired,
}

export function AppRoutes() {
  const { user, loading } = useAuth()

  if (loading) {
    return <LoadingFallback />
  }

  return (
    <Routes>
      <Route
        path={AUTH_ROUTES.LOGIN}
        element={
          user ? (
            <Navigate to={ALL_SITES_PATH} replace />
          ) : (
            <Page>
              <LoginPage />
            </Page>
          )
        }
      />
      <Route
        path={APP_ROUTES.ROOT}
        element={
          <ProtectedRoute>
            <Navigate to={ALL_SITES_PATH} replace />
          </ProtectedRoute>
        }
      />
      <Route
        path={`${APP_ROUTES.DASHBOARD}/*`}
        element={
          <ProtectedRoute>
            <Page>
              <DashboardLayout />
            </Page>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to={DASHBOARD_ROUTES.ALL_SITES} replace />} />
        <Route
          path={DASHBOARD_ROUTES.ALL_SITES}
          element={
            <Page>
              <AllSites />
            </Page>
          }
        />
        <Route
          path={DASHBOARD_ROUTES.SITE}
          element={
            <Page>
              <SiteDashboard />
            </Page>
          }
        />
        <Route
          path={DASHBOARD_ROUTES.CONTROL_STATION}
          element={
            <Page>
              <ControlStationDashboard />
            </Page>
          }
        />
        <Route
          path={DASHBOARD_ROUTES.VEHICLE}
          element={
            <Page>
              <VehicleDashboard />
            </Page>
          }
        />
        <Route path='*' element={<Navigate to={DASHBOARD_ROUTES.ALL_SITES} replace />} />
      </Route>
    </Routes>
  )
}
